import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, User, Mail, Phone, MessageSquare, CheckCircle2, XCircle, AlertCircle, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: '#F59E0B', bg: 'rgba(245,158,11,0.1)', icon: AlertCircle },
  confirmed: { label: 'Confirmed', color: '#10B981', bg: 'rgba(16,185,129,0.1)', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: '#EF4444', bg: 'rgba(239,68,68,0.1)', icon: XCircle },
};

const BookingsInbox = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const res = await axios.get(`${API}/bookings`);
      setBookings(res.data);
    } catch (err) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/bookings/${id}/status`, { status });
      setBookings(prev => prev.map(b => b.id === id ? { ...b, status } : b));
      toast.success(`Booking ${status}`);
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const filtered = filter === 'all' ? bookings : bookings.filter(b => b.status === filter);

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <h1 className="font-outfit font-bold text-2xl text-white">Bookings</h1>
        <p className="text-[#A1A1AA] mt-1">Manage your appointments</p>
      </div>

      <div className="flex gap-2 mb-6">
        {['all', 'pending', 'confirmed', 'cancelled'].map(f => (
          <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-all ${filter === f ? 'bg-violet-500 text-white' : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'}`}>
            {f === 'all' ? `All (${bookings.length})` : `${f} (${bookings.filter(b => b.status === f).length})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#525252]">Loading...</div>
      ) : filtered.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-2xl bg-[#121212] border border-[#27272A] p-12 text-center">
          <Calendar className="w-10 h-10 text-[#27272A] mx-auto mb-3" />
          <p className="text-[#525252] text-sm">No bookings yet</p>
        </motion.div>
      ) : (
        <div className="space-y-3">
          {filtered.map((booking, i) => {
            const sc = STATUS_CONFIG[booking.status] || STATUS_CONFIG.pending;
            const StatusIcon = sc.icon;
            return (
              <motion.div key={booking.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                data-testid={`booking-${booking.id}`} className="rounded-xl bg-[#121212] border border-[#27272A] p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: sc.bg }}>
                        <StatusIcon className="w-4 h-4" style={{ color: sc.color }} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white text-sm">{booking.name}</h3>
                        <div className="flex items-center gap-2 text-xs text-[#71717A]">
                          <Calendar className="w-3 h-3" /> {booking.date}
                          <Clock className="w-3 h-3 ml-1" /> {booking.time_slot}
                        </div>
                      </div>
                    </div>

                    <div className="ml-12 space-y-1">
                      {booking.service_name && booking.service_name !== 'General' && (
                        <p className="text-sm text-violet-400 font-medium">{booking.service_name}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 text-sm text-[#A1A1AA]">
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{booking.email}</span>
                        {booking.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{booking.phone}</span>}
                      </div>
                      {booking.message && (
                        <p className="text-sm text-[#71717A] mt-1 flex items-start gap-1">
                          <MessageSquare className="w-3 h-3 mt-0.5 shrink-0" />{booking.message}
                        </p>
                      )}
                    </div>
                  </div>

                  {booking.status === 'pending' && (
                    <div className="flex gap-2 shrink-0">
                      <Button data-testid={`confirm-${booking.id}`} size="sm" onClick={() => updateStatus(booking.id, 'confirmed')}
                        className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded-lg text-xs">
                        Confirm
                      </Button>
                      <Button data-testid={`cancel-${booking.id}`} size="sm" onClick={() => updateStatus(booking.id, 'cancelled')}
                        className="bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg text-xs">
                        Cancel
                      </Button>
                    </div>
                  )}
                  {booking.status !== 'pending' && (
                    <div className="flex items-center gap-2 shrink-0">
                      <a data-testid={`ics-${booking.id}`} href={`${API}/bookings/${booking.id}/ics`} download
                        className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-[#A1A1AA] hover:text-white transition-colors" title="Add to Calendar">
                        <Download className="w-4 h-4" />
                      </a>
                      <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ backgroundColor: sc.bg, color: sc.color }}>
                        {sc.label}
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BookingsInbox;
